import React from "react";
import { Checkbox } from "./Checkbox";

/** Percentages offered at payment time. -1 is the "Otro" custom-amount button. */
export const tipPercentageOptions: number[] = [5, 10, 12, 15, 20, 25, 30, -1];

/** Stored on its own when the entity offers no tips; holding none means it inherits instead. */
export const TIP_NO_TIPS_PERCENT: number = 0;

export interface TipPercentageFieldsProps {
  disabled: boolean;
  tips: number[];
  onChange: (tips: number[]) => void;
  /** Whether the entity can defer to the one above it. Off for clients. */
  showInheritToggle?: boolean;
  /** What inheriting means for this entity, e.g. "usa las propinas del cliente". */
  inheritLabel?: string;
}

/**
 * Tip percentages of a client, concession or seller. No percentages inherits, [0] means no tips.
 */
export const TipPercentageFields: React.FC<TipPercentageFieldsProps> = (
  props,
) => {
  const {
    disabled,
    tips,
    onChange,
    showInheritToggle = false,
    inheritLabel = "usa las propinas del nivel superior",
  } = props;

  const inherits: boolean = showInheritToggle && tips.length === 0;
  const selected: number[] = tips.filter(
    (tip: number) => tip !== TIP_NO_TIPS_PERCENT,
  );
  const offersNoTips: boolean = !inherits && selected.length === 0;

  /** Stores the picked percentages. An entity that can inherit needs [0] to say "no tips". */
  const savePercentages = (percentages: number[]): void => {
    if (percentages.length > 0) {
      onChange(percentages);
      return;
    }

    onChange(showInheritToggle ? [TIP_NO_TIPS_PERCENT] : []);
  };

  const togglePercentage = (option: number): void => {
    savePercentages(
      selected.includes(option)
        ? selected.filter((tip: number) => tip !== option)
        : [...selected, option],
    );
  };

  const toggleInherit = (): void => {
    // Checking it drops the entity's own percentages; unchecking it starts from "no tips"
    onChange(inherits ? [TIP_NO_TIPS_PERCENT] : []);
  };

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-semibold text-gray-800">
        Configuración de Propinas
      </h3>
      {showInheritToggle && (
        <Checkbox
          checked={inherits}
          disabled={disabled}
          label={`Heredar porcentajes de propina (${inheritLabel})`}
          onChange={toggleInherit}
        />
      )}
      <div className="flex flex-col gap-2">
        <label className="text-sm text-gray-500">Porcentajes de Propina</label>
        <div className="flex flex-wrap gap-3">
          {tipPercentageOptions.map((option: number) => (
            <button
              key={option}
              className={`rounded-lg px-6 py-3 text-sm transition-colors disabled:opacity-50 ${
                selected.includes(option)
                  ? "border border-black bg-black text-white"
                  : "border border-gray-200 bg-white text-gray-700 hover:border-gray-400 disabled:border-gray-200"
              }`}
              disabled={disabled || inherits}
              onClick={() => togglePercentage(option)}
            >
              {`${option === -1 ? "Otro" : `${option}%`}`}
            </button>
          ))}
        </div>
        {inherits && (
          <p className="text-xs text-gray-500">
            {`Sin porcentajes propios: ${inheritLabel}.`}
          </p>
        )}
        {offersNoTips && (
          <p className="text-xs text-gray-500">
            No se solicitarán propinas en la terminal.
          </p>
        )}
      </div>
    </div>
  );
};
